'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useBreakpoint } from '../../../utils/useBreakpoint';

const TABLET_MAX_HEIGHT = 600;
const MOBILE_MAX_HEIGHT = 512;

const COLLAPSE_CLASS =
  'max-h-[512px] md:max-h-[600px] lg:max-h-none overflow-hidden';

export const useHeightCollapse = () => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [shouldCollapse, setShouldCollapse] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const collapseHeight = useBreakpoint<number>({
    _: MOBILE_MAX_HEIGHT,
    md: TABLET_MAX_HEIGHT,
    lg: Infinity,
  });

  const checkHeight = useCallback(() => {
    const el = contentRef.current;
    if (!el || !isFinite(collapseHeight)) {
      setShouldCollapse(false);
      return;
    }

    setShouldCollapse(el.scrollHeight > collapseHeight);
  }, [collapseHeight]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const observer = new ResizeObserver(checkHeight);
    observer.observe(el);
    checkHeight();

    return () => {
      observer.disconnect();
    };
  }, [checkHeight]);

  const expand = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const isCollapsed = shouldCollapse && !isExpanded;
  const collapseClass = isCollapsed ? COLLAPSE_CLASS : '';

  return {
    contentRef,
    isCollapsed,
    isExpanded,
    expand,
    collapseClass,
  };
};
